import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Bot, ShieldCheck, ShieldAlert, Loader2 } from 'lucide-react';

/**
 * OAuth 2.1 consent screen shared by BOTH Claude MCP connectors:
 * "Claude Code Access" and "Dashboard Data Access". Reached via a 302 from
 * GET /api/oauth/authorize. This app authenticates with a JWT in
 * localStorage (not cookie sessions), so any client-side gating + the
 * actual "Approve/Deny" decision both happen here, where that token lives —
 * App.js already redirects unauthenticated users to /login?returnTo=...
 * before this component ever mounts.
 *
 * IMPORTANT (Sept 2026 fix): this component originally only ever served
 * Code Access and unconditionally required user.role === 'super_admin'
 * before showing the approve/deny screen at all, regardless of which
 * `resource` was actually being requested. When Dashboard Data Access (with
 * its own per-user grant system, enforced server-side in
 * mcp_code_access.py's oauth_authorize_decision) was added, this screen was
 * never updated — so any non-super-admin user with a perfectly valid Data
 * Access grant was blocked here before their request ever reached the
 * server's correct, resource-aware check. Fix: gate on super-admin status
 * ONLY for the Code Access resource; any other authenticated user is
 * allowed through to the consent screen for Data Access (the server still
 * independently validates their grant when the decision is submitted).
 */
const OAuthConsent = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(null); // 'approve' | 'deny' | null
  const [approveError, setApproveError] = useState('');

  const params = new URLSearchParams(location.search);
  const clientName = params.get('client_name') || 'Unknown app';
  const clientId = params.get('client_id') || '';
  const redirectUri = params.get('redirect_uri') || '';
  const codeChallenge = params.get('code_challenge') || '';
  const resource = params.get('resource') || '';
  const state = params.get('state') || '';

  // Mirrors backend/helpers/mcp_resources.py's two resource URLs exactly
  // (API here is `${REACT_APP_BACKEND_URL}/api`, same as BASE_URL there).
  const isDataAccessResource = resource === `${API}/mcp/data-access`;
  // Fail closed: only the confirmed Data Access resource skips the
  // super-admin gate. Code Access, and any unrecognised resource, still
  // require it — matching the server's own default-deny for unknown
  // resources in oauth_authorize_start/oauth_authorize_decision.
  const requiresSuperAdmin = !isDataAccessResource;

  const isSuperAdmin = user?.role === 'super_admin';

  const decide = async (decision) => {
    setSubmitting(decision);
    setApproveError('');
    try {
      const res = await axios.post(`${API}/oauth/authorize`, {
        decision,
        client_id: clientId,
        redirect_uri: redirectUri,
        code_challenge: codeChallenge,
        resource,
        state,
      });
      window.location.href = res.data.redirect_url;
    } catch (e) {
      setSubmitting(null);
      const detail = e?.response?.data?.detail;
      if (decision === 'approve' && detail) {
        // e.g. "No Dashboard Data Access grant configured for your account —
        // ask a super admin to add one in Settings > Integrations first."
        setApproveError(detail);
      } else {
        navigate('/');
      }
    }
  };

  useEffect(() => {
    if (!clientId || !redirectUri) {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (requiresSuperAdmin && !isSuperAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-testid="oauth-consent-denied">
        <Card className="max-w-md w-full border-red-100">
          <CardHeader className="flex flex-col items-center text-center">
            <ShieldAlert className="w-10 h-10 text-red-500 mb-2" />
            <CardTitle>Super Admin access required</CardTitle>
            <CardDescription>
              You're signed in as {user?.email}, but only a super admin can approve Claude Code Access connections.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full" onClick={() => navigate('/')} data-testid="oauth-consent-denied-home-btn">
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const connectorLabel = isDataAccessResource ? 'Dashboard Data Access' : 'Claude Code Access';
  const scopeDescription = isDataAccessResource
    ? `This will let ${clientName} read data from your live dashboard (orders, transport, sales, CRM, etc. — scoped to whatever branches and settings access your account has been granted, never secrets or credentials) for as long as this connection stays approved.`
    : `This will let ${clientName} read your source code (frontend + backend files only — never secrets, .env files, or business data) for as long as this connection stays approved.`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-testid="oauth-consent-page">
      <Card className="max-w-md w-full shadow-lg">
        <CardHeader className="flex flex-col items-center text-center gap-1">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-2"
            style={{ background: 'radial-gradient(circle at 50% 50%, #ffffff 0%, #ffe3c2 30%, #f4a35f 70%, #ee7203 100%)' }}
          >
            <Bot className="w-7 h-7" style={{ color: '#ee7203' }} />
          </div>
          <CardTitle className="text-xl" data-testid="oauth-consent-client-name">{clientName}</CardTitle>
          <CardDescription>wants to connect via {connectorLabel}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 text-sm text-gray-600 flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
            <span>{scopeDescription}</span>
          </div>
          {approveError && (
            <div className="rounded-lg bg-red-50 border border-red-100 p-3 text-sm text-red-700" data-testid="oauth-consent-approve-error">
              {approveError}
            </div>
          )}
          <p className="text-xs text-gray-400 text-center">
            Approving as <strong>{user?.email}</strong>{isSuperAdmin ? ' (super admin)' : ''}
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline" className="flex-1"
              onClick={() => decide('deny')} disabled={!!submitting}
              data-testid="oauth-consent-deny-btn"
            >
              {submitting === 'deny' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Deny'}
            </Button>
            <Button
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => decide('approve')} disabled={!!submitting}
              data-testid="oauth-consent-approve-btn"
            >
              {submitting === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Approve'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OAuthConsent;
